import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PortalIcon from './PortalIcon';
import PortalPaymentModal from './PortalPaymentModal';
import {
  formatDate,
  formatMoney,
  normalizeToken,
  orderPaymentPending,
  statusTone,
} from '../lib/portal';

function Badge({ tone, children }) {
  return <span className={`portal-badge portal-badge--${tone}`}>{children}</span>;
}

function isPaidCollection(collection) {
  return normalizeToken(collection?.estado) === 'PAGADA';
}

function orderLabel(order) {
  return `#${order?.tiendaNubeOrderNumber || order?.tiendaNubeOrderId || order?.id}`;
}

function LocalityField({ value, postalCode, onChange, onSelect, authRequestJson }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const handleChange = (event) => {
    const next = event.target.value;
    onChange(next, postalCode);
    setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!next.trim()) { setSuggestions([]); return; }
      try {
        const result = await authRequestJson(`/portal/localidades?q=${encodeURIComponent(next)}&take=80`);
        setSuggestions(Array.isArray(result?.data) ? result.data : []);
      } catch { setSuggestions([]); }
    }, 240);
  };

  return (
    <div className="portal-field">
      <label className="portal-field-label" htmlFor="retiro-localidad">Localidad de retiro</label>
      <input id="retiro-localidad" value={value} onChange={handleChange} onFocus={() => setOpen(true)} autoComplete="off" placeholder="Buscá una localidad" />
      {open && suggestions.length > 0 ? (
        <div className="portal-suggestion-list">
          {suggestions.map((locality, index) => (
            <button key={`${locality.codigoPostal || index}-${locality.nombre || index}`} type="button" onClick={() => { onSelect(locality); setSuggestions([]); setOpen(false); }}>
              {locality.nombre || locality.display} <span className="portal-muted">CP {locality.codigoPostal || '—'}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ColectaEditor({ initial, editing, onSaved, onCancel }) {
  const { authRequestJson } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [form, setForm] = useState({
    retiroNombreSitio: '',
    retiroDireccion: '',
    retiroLocalidad: '',
    retiroCodigoPostal: '',
    retiroNotas: '',
    atiendeTitular: true,
    terceroNombre: '',
    terceroDni: '',
    terceroTelefono: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const requests = [authRequestJson('/portal/pedidos-pendientes-colecta?take=300'), authRequestJson('/portal/me')];
        const [availableResponse, meResponse] = await Promise.all(requests);
        const available = Array.isArray(availableResponse?.data) ? availableResponse.data : [];
        const current = Array.isArray(initial?.pedidos) ? initial.pedidos : [];
        const merged = [...current, ...available].filter((order, index, list) => list.findIndex((item) => Number(item.id) === Number(order.id)) === index);
        if (!active) return;
        setOrders(merged);
        setSelectedIds(new Set(current.map((order) => Number(order.id))));
        setForm((previous) => ({
          ...previous,
          retiroNombreSitio: initial?.retiroNombreSitio || meResponse?.data?.cliente?.razonSocial || previous.retiroNombreSitio,
          retiroDireccion: initial?.retiroDireccion || meResponse?.data?.cliente?.domicilio || previous.retiroDireccion,
          retiroLocalidad: initial?.retiroLocalidad || previous.retiroLocalidad,
          retiroCodigoPostal: initial?.retiroCodigoPostal || meResponse?.data?.cliente?.codigoPostal || previous.retiroCodigoPostal,
          retiroNotas: initial?.retiroNotas || previous.retiroNotas,
          atiendeTitular: initial?.atiendeTitular ?? previous.atiendeTitular,
          terceroNombre: initial?.terceroNombre || previous.terceroNombre,
          terceroDni: initial?.terceroDni || previous.terceroDni,
          terceroTelefono: initial?.terceroTelefono || previous.terceroTelefono,
        }));
      } catch (requestError) {
        if (active) setError(requestError.message || 'No se pudieron cargar los pedidos disponibles.');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [authRequestJson, initial]);

  const selectedOrders = useMemo(() => orders.filter((order) => selectedIds.has(Number(order.id))), [orders, selectedIds]);
  const pendingOrders = selectedOrders.filter(orderPaymentPending);
  const pendingShippingTotal = pendingOrders.reduce((total, order) => total + Number(order.valorViaje || 0), 0);
  const paidInside = selectedOrders.length - pendingOrders.length;

  const change = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));
  const toggle = (id) => setSelectedIds((previous) => {
    const next = new Set(previous);
    const numeric = Number(id);
    if (next.has(numeric)) next.delete(numeric); else next.add(numeric);
    return next;
  });

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (selectedIds.size === 0) { setError('Seleccioná al menos un pedido.'); return; }
    if (!form.retiroDireccion.trim()) { setError('La dirección de retiro es obligatoria.'); return; }
    setSaving(true);
    try {
      const pedidoIds = Array.from(selectedIds);
      const pedidoIdsCobro = pendingOrders.map((order) => Number(order.id));
      const payload = {
        pedidoIds,
        pedidoIdsCobro,
        modoCobro: 'DIVIDIDO',
        ...(editing ? {} : form),
      };
      const response = await authRequestJson(editing ? `/portal/colectas/${initial.id}/pedidos` : '/portal/colectas', {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });
      onSaved(response?.data || {});
    } catch (requestError) {
      setError(requestError.message || 'No se pudo guardar la colecta.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="portal-app">
      <div className="portal-wrap">
        <header className="portal-page-header">
          <Link className="portal-btn portal-btn--outline portal-btn--small" to="/colectas"><PortalIcon name="back" size={15} /> Volver a colectas</Link>
          <p className="portal-kicker" style={{ marginTop: 20 }}>Colectas / {editing ? 'Editar selección' : 'Nueva solicitud'}</p>
          <h1>{editing ? 'Ajustar colecta' : 'Solicitar una colecta'}</h1>
          <p>Completá los datos del retiro, seleccioná los pedidos y confirmá el importe que el sistema recalcula con el tarifario vigente.</p>
        </header>

        {error ? <div className="portal-alert portal-alert--error">{error}</div> : null}
        {loading ? <section className="portal-card portal-loading">Cargando pedidos disponibles…</section> : (
          <form className="portal-card portal-form-card portal-form" onSubmit={submit}>
            <section className="portal-form-section">
              <h3>1 / Datos del retiro</h3>
              <div className="portal-form-grid">
                <div className="portal-field"><label className="portal-field-label" htmlFor="retiro-sitio">Nombre del sitio</label><input id="retiro-sitio" value={form.retiroNombreSitio} onChange={(event) => change('retiroNombreSitio', event.target.value)} placeholder="Ej. Depósito central" /></div>
                <div className="portal-field"><label className="portal-field-label" htmlFor="retiro-direccion">Dirección *</label><input id="retiro-direccion" value={form.retiroDireccion} onChange={(event) => change('retiroDireccion', event.target.value)} placeholder="Calle y altura" required /></div>
                <LocalityField value={form.retiroLocalidad} postalCode={form.retiroCodigoPostal} authRequestJson={authRequestJson} onChange={(locality, postalCode) => setForm((previous) => ({ ...previous, retiroLocalidad: locality, retiroCodigoPostal: postalCode }))} onSelect={(locality) => setForm((previous) => ({ ...previous, retiroLocalidad: locality.nombre || locality.display || '', retiroCodigoPostal: locality.codigoPostal || '' }))} />
                <div className="portal-field"><label className="portal-field-label" htmlFor="retiro-cp">Código postal *</label><input id="retiro-cp" value={form.retiroCodigoPostal} onChange={(event) => change('retiroCodigoPostal', event.target.value)} required /></div>
                <div className="portal-field portal-field--wide"><label className="portal-field-label" htmlFor="retiro-notas">Indicaciones para el retiro</label><textarea id="retiro-notas" value={form.retiroNotas} onChange={(event) => change('retiroNotas', event.target.value)} placeholder="Horarios, referencias o información útil para el chofer" /></div>
              </div>
            </section>

            <section className="portal-form-section">
              <h3>2 / Persona que entrega</h3>
              <div className="portal-actions" style={{ marginBottom: 15 }}>
                <label className="portal-radio"><input type="radio" checked={form.atiendeTitular} onChange={() => change('atiendeTitular', true)} /> Titular de la cuenta</label>
                <label className="portal-radio"><input type="radio" checked={!form.atiendeTitular} onChange={() => change('atiendeTitular', false)} /> Otra persona</label>
              </div>
              {!form.atiendeTitular ? <div className="portal-form-grid portal-form-grid--three"><div className="portal-field"><label className="portal-field-label">Nombre</label><input value={form.terceroNombre} onChange={(event) => change('terceroNombre', event.target.value)} /></div><div className="portal-field"><label className="portal-field-label">DNI</label><input value={form.terceroDni} onChange={(event) => change('terceroDni', event.target.value)} /></div><div className="portal-field"><label className="portal-field-label">Teléfono</label><input value={form.terceroTelefono} onChange={(event) => change('terceroTelefono', event.target.value)} /></div></div> : null}
            </section>

            <section className="portal-form-section">
              <div className="portal-select-list-head"><div><h3>3 / Pedidos de la colecta</h3><p>Se pueden mezclar pedidos con flete ya abonado y flete pendiente.</p></div><div className="portal-actions"><button type="button" className="portal-btn portal-btn--outline portal-btn--small" onClick={() => setSelectedIds(new Set(orders.map((order) => Number(order.id))))}>Todos</button><button type="button" className="portal-btn portal-btn--outline portal-btn--small" onClick={() => setSelectedIds(new Set())}>Limpiar</button></div></div>
              <div className="portal-select-list" style={{ marginTop: 15 }}>
                {orders.length === 0 ? <div className="portal-empty"><strong>No hay pedidos disponibles.</strong><p>Los pedidos aparecen cuando están pagos en Tiendanube y sincronizados con ESA.</p></div> : orders.map((order) => {
                  const selected = selectedIds.has(Number(order.id));
                  const paid = !orderPaymentPending(order);
                  return <label className={`portal-check-row ${selected ? 'is-selected' : ''}`} key={order.id}><input type="checkbox" checked={selected} onChange={() => toggle(order.id)} /><strong>{orderLabel(order)}<span className="portal-muted">{order.localidadDestino || 'Destino pendiente'}</span></strong><span>{order.contactName || order.contactEmail || 'Cliente Tiendanube'}</span><span>{paid ? 'Ya abonado' : formatMoney(order.valorViaje)}</span><span className={`portal-badge portal-badge--${paid ? 'success' : 'warning'}`}>{paid ? 'Flete abonado' : 'Pendiente'}</span></label>;
                })}
              </div>
            </section>

            <section className="portal-form-section">
              <div className="portal-detail-grid">
                <div className="portal-detail-item"><span>Pedidos seleccionados</span><strong>{selectedOrders.length}</strong></div>
                <div className="portal-detail-item"><span>Fletes pendientes</span><strong>{formatMoney(pendingShippingTotal)}</strong></div>
                <div className="portal-detail-item"><span>Pedidos ya abonados</span><strong>{paidInside}</strong></div>
                <div className="portal-detail-item"><span>Total definitivo</span><strong>Se recalcula al confirmar</strong></div>
              </div>
              <p className="portal-field-hint" style={{ marginTop: 16 }}>El pago de la colecta suma únicamente los fletes pendientes más el valor del retiro. Los fletes ya abonados se incluyen con importe cero.</p>
            </section>

            <footer className="portal-form-footer"><button type="button" className="portal-btn portal-btn--outline" onClick={onCancel}>Cancelar</button><button type="submit" className="portal-btn" disabled={saving}>{saving ? 'Recalculando…' : editing ? 'Guardar cambios' : 'Crear y pasar a pagar'}</button></footer>
          </form>
        )}
      </div>
    </div>
  );
}

function ColectaDetail({ collection, onPay, onDelete, onEdit }) {
  const navigate = useNavigate();
  const paid = isPaidCollection(collection);
  const cancelled = normalizeToken(collection?.estado) === 'CANCELADA';
  const amount = collection?.montoPreferenciaMp || collection?.montoCotizado || 0;
  const orders = Array.isArray(collection?.pedidos) ? collection.pedidos : [];

  return (
    <div className="portal-app">
      <div className="portal-wrap">
        <div className="portal-page-header">
          <Link className="portal-btn portal-btn--outline portal-btn--small" to="/colectas"><PortalIcon name="back" size={15} /> Volver a colectas</Link>
        </div>
        <div className="portal-detail-layout">
          <section className="portal-card portal-detail-main">
            <div className="portal-detail-topline"><div><p className="portal-kicker">Solicitud de colecta</p><h2>{collection.numero || `Colecta #${collection.id}`}</h2><p>Creada el {formatDate(collection.creadoEn)}</p></div><Badge tone={statusTone(collection.estado)}>{collection.estado || 'Pendiente'}</Badge></div>
            <div className="portal-detail-grid"><div className="portal-detail-item"><span>Sitio</span><strong>{collection.retiroNombreSitio || '—'}</strong></div><div className="portal-detail-item"><span>Dirección</span><strong>{collection.retiroDireccion || '—'}</strong></div><div className="portal-detail-item"><span>Localidad</span><strong>{collection.retiroLocalidad || '—'} · CP {collection.retiroCodigoPostal || '—'}</strong></div><div className="portal-detail-item"><span>Estado Saadis</span><strong>{collection.saadisEstado || 'Pendiente'}</strong></div></div>
            <div className="portal-form-section"><div className="portal-section-title"><div><p className="portal-kicker">Contenido</p><h2>Pedidos incluidos</h2></div><span className="portal-muted">{orders.length} pedido{orders.length === 1 ? '' : 's'}</span></div><div className="portal-table-wrap"><table className="portal-table"><thead><tr><th>Pedido</th><th>Destino</th><th>Flete ESA</th><th>Estado</th><th /></tr></thead><tbody>{orders.map((order) => <tr key={order.id}><td><strong>{orderLabel(order)}</strong><span className="portal-muted">{order.contactName || order.contactEmail || 'Cliente Tiendanube'}</span></td><td>{order.localidadDestino || '—'}</td><td>{orderPaymentPending(order) ? formatMoney(order.valorViaje) : 'Ya abonado'}</td><td><Badge tone={statusTone(order.cobroEstado)}>{orderPaymentPending(order) ? 'Pendiente' : 'Abonado'}</Badge></td><td><button type="button" className="portal-btn portal-btn--outline portal-btn--small" onClick={() => navigate(`/pedidos/${order.id}`)}>Detalle</button></td></tr>)}</tbody></table></div></div>
          </section>
          <aside className="portal-card portal-detail-side"><div className="portal-side-stat"><span>Importe de retiro</span><strong>{formatMoney(collection.montoCotizado)}</strong></div><div className="portal-side-stat"><span>Total a pagar</span><strong>{formatMoney(amount)}</strong></div><div className="portal-side-stat"><span>Bultos / peso</span><strong>{collection.bultosTotal || 0} · {Number(collection.kgTotal || 0).toLocaleString('es-AR')} kg</strong></div><div className="portal-actions" style={{ marginTop: 24 }}>{!paid && !cancelled ? <><button type="button" className="portal-btn portal-btn--mint" onClick={onPay}><PortalIcon name="card" size={16} /> Pagar colecta</button><button type="button" className="portal-btn portal-btn--outline" onClick={onEdit}>Editar pedidos</button><button type="button" className="portal-btn portal-btn--danger" onClick={onDelete}>Eliminar solicitud</button></> : null}</div></aside>
        </div>
      </div>
    </div>
  );
}

export default function PortalColectas() {
  const { authRequestJson } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payTarget, setPayTarget] = useState(null);

  const isNew = location.pathname.endsWith('/nueva');
  const isEdit = location.pathname.endsWith('/editar');
  const collectionId = params.id;
  const detailId = !isNew && !isEdit ? collectionId : null;

  const loadCollections = useCallback(async () => {
    const response = await authRequestJson('/portal/colectas?take=500');
    const list = Array.isArray(response?.data) ? response.data : [];
    setCollections(list);
    return list;
  }, [authRequestJson]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await loadCollections();
        if (collectionId) {
          const detail = await authRequestJson(`/portal/colectas/${collectionId}`);
          if (active) setSelected(detail?.data || list.find((item) => String(item.id) === String(collectionId)) || null);
        }
      } catch (requestError) {
        if (active) setError(requestError.message || 'No se pudieron cargar las colectas.');
      } finally {
        if (active) setLoading(false);
      }
    };
    if (!isNew) void load();
    else setLoading(false);
    return () => { active = false; };
  }, [authRequestJson, collectionId, isEdit, isNew, loadCollections]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return collections.filter((collection) => (!needle || [collection.numero, collection.retiroNombreSitio, collection.retiroLocalidad].some((value) => String(value || '').toLowerCase().includes(needle))) && (statusFilter === 'all' || normalizeToken(collection.estado).toLowerCase() === statusFilter));
  }, [collections, query, statusFilter]);

  const onSaved = async (data) => {
    await loadCollections().catch(() => {});
    navigate(`/colectas/${data.id}`);
    if (!isEdit) setPayTarget(data);
  };

  const remove = async () => {
    if (!selected || !window.confirm('¿Eliminar esta solicitud de colecta? Los pedidos volverán a quedar disponibles.')) return;
    try {
      await authRequestJson(`/portal/colectas/${selected.id}`, { method: 'DELETE' });
      navigate('/colectas');
    } catch (requestError) { setError(requestError.message || 'No se pudo eliminar la colecta.'); }
  };

  const removeById = async (collection) => {
    if (!window.confirm(`¿Eliminar la solicitud ${collection.numero || collection.id}? Los pedidos volverán a quedar disponibles.`)) return;
    try {
      setError('');
      await authRequestJson(`/portal/colectas/${collection.id}`, { method: 'DELETE' });
      await loadCollections();
    } catch (requestError) { setError(requestError.message || 'No se pudo eliminar la colecta.'); }
  };

  if (isNew || isEdit) return <ColectaEditor initial={isEdit ? selected : null} editing={isEdit} onSaved={onSaved} onCancel={() => navigate(isEdit ? `/colectas/${params.id}` : '/colectas')} />;

  if (detailId) {
    if (loading) return <div className="portal-app"><div className="portal-wrap portal-loading">Cargando la colecta…</div></div>;
    if (!selected) return <div className="portal-app"><div className="portal-wrap"><div className="portal-error-panel"><strong>Colecta no encontrada.</strong>{error}</div></div></div>;
    return <><ColectaDetail collection={selected} onPay={() => setPayTarget(selected)} onDelete={() => void remove()} onEdit={() => navigate(`/colectas/${selected.id}/editar`)} />{payTarget ? <PortalPaymentModal title={`Pagar ${selected.numero || 'colecta'}`} amount={payTarget.montoPreferenciaMp || payTarget.totalPreferencia || payTarget.montoCotizado} preferencePath={`/portal/colectas/${payTarget.id}/pagos/mp/preference`} onClose={() => setPayTarget(null)} /> : null}</>;
  }

  return (
    <div className="portal-app">
      <div className="portal-wrap">
        <header className="portal-page-header--split"><div><p className="portal-kicker">Operación / Colectas</p><h1>Solicitudes de colecta</h1><p>Armá retiros, revisá los pedidos incluidos y aboná cada solicitud desde el portal.</p></div><Link className="portal-btn" to="/colectas/nueva"><PortalIcon name="plus" size={17} /> Nueva colecta</Link></header>
        {error ? <div className="portal-alert portal-alert--error">{error}</div> : null}
        <div className="portal-filterbar"><label className="portal-search"><PortalIcon name="search" size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por número, sitio o localidad" /></label><select className="portal-control portal-filter-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filtrar colectas"><option value="all">Todos los estados</option><option value="aguardando_pago">Aguardando pago</option><option value="pagada">Pagadas</option><option value="cancelada">Canceladas</option></select></div>
        <section className="portal-card portal-table-card">{loading ? <div className="portal-loading">Cargando colectas…</div> : null}{!loading && filtered.length === 0 ? <div className="portal-empty"><strong>Todavía no hay solicitudes.</strong><p>Creá la primera colecta con los pedidos pagados que quieras retirar.</p></div> : null}{!loading && filtered.length > 0 ? <div className="portal-table-wrap"><table className="portal-table"><thead><tr><th>Solicitud</th><th>Retiro</th><th>Pedidos</th><th>Importe</th><th>Estado</th><th>Creada</th><th /></tr></thead><tbody>{filtered.map((collection) => <tr key={collection.id} data-clickable="true" onClick={() => navigate(`/colectas/${collection.id}`)}><td><strong>{collection.numero || `Colecta #${collection.id}`}</strong><span className="portal-muted">ID interno {collection.id}</span></td><td>{collection.retiroNombreSitio || 'Sin nombre'}<span className="portal-muted">{collection.retiroLocalidad || 'Localidad pendiente'}</span></td><td>{Array.isArray(collection.pedidos) ? collection.pedidos.length : collection.pedidoIds?.length || 0}</td><td>{formatMoney(collection.montoPreferenciaMp || collection.montoCotizado)}</td><td><Badge tone={statusTone(collection.estado)}>{collection.estado || 'Pendiente'}</Badge></td><td>{formatDate(collection.creadoEn)}</td><td><div className="portal-actions"><button type="button" className="portal-btn portal-btn--outline portal-btn--small" onClick={(event) => { event.stopPropagation(); navigate(`/colectas/${collection.id}`); }}>Detalle</button>{!isPaidCollection(collection) && normalizeToken(collection.estado) !== 'CANCELADA' ? <button type="button" className="portal-btn portal-btn--danger portal-btn--small" onClick={(event) => { event.stopPropagation(); void removeById(collection); }}>Eliminar</button> : null}</div></td></tr>)}</tbody></table></div> : null}</section>
      </div>
    </div>
  );
}
